import { useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Screen from '../components/ui/Screen'
import BottomNav from '../components/ui/BottomNav'
import TopBar from '../components/ui/TopBar'
import Panel from '../components/ui/Panel'
import Chip from '../components/ui/Chip'
import Avatar from '../components/ui/Avatar'
import Input from '../components/ui/Input'
import Button from '../components/ui/Button'
import QrCode from '../components/ui/QrCode'
import { useToast } from '../components/ui/Toast'
import texts from '../texts'
import {
  addMember,
  deleteClub,
  getCandidates,
  getClub,
  inviteByEmail,
  inviteUrl,
  leaveClub,
  removeMember,
  renameClub,
  rotateInvite,
  transferClub
} from '../lib/api'
import type { ClubCandidate, ClubDetails } from '../lib/api'
import CsvButton from '../components/CsvButton'
import { clubExportCsvUrl } from '../lib/api.share'
import shareTexts from '../texts.share'

function fmt(n: number): string {
  return (n > 0 ? '+' : '') + n
}

function Club() {
  const { id } = useParams<{ id: string }>()
  const clubId = Number(id)
  const navigate = useNavigate()
  const { showToast } = useToast()
  const [club, setClub] = useState<ClubDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [renaming, setRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [confirmingRotate, setConfirmingRotate] = useState(false)
  const [confirmingLeave, setConfirmingLeave] = useState(false)
  const [removingUserId, setRemovingUserId] = useState<number | null>(null)
  const [error, setError] = useState('')
  const [inviteEmail, setInviteEmail] = useState('')
  const [invitingEmail, setInvitingEmail] = useState(false)
  const [candidates, setCandidates] = useState<ClubCandidate[]>([])
  const [addingUserId, setAddingUserId] = useState<number | null>(null)
  const [transferUserId, setTransferUserId] = useState<number | null>(null)
  const [confirmingTransfer, setConfirmingTransfer] = useState(false)
  const [transferring, setTransferring] = useState(false)
  const [confirmingDeleteClub, setConfirmingDeleteClub] = useState(false)
  const [deletingClub, setDeletingClub] = useState(false)
  const linkRef = useRef<HTMLSpanElement>(null)

  const currentUserId = parseInt(localStorage.getItem('userId') || '0', 10)

  const load = () => {
    getClub(clubId)
      .then(setClub)
      .finally(() => setLoading(false))
    getCandidates(clubId)
      .then(setCandidates)
      .catch(() => {
        // Listen forbliver tom, hvis den ikke kan hentes
      })
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId])

  if (loading) {
    return (
      <Screen>
        <TopBar title={texts.club.loading} back="/klubber" />
        <BottomNav current="clubs" />
      </Screen>
    )
  }

  if (!club) {
    return (
      <Screen>
        <TopBar title={texts.club.genericError} back="/klubber" />
        <BottomNav current="clubs" />
      </Screen>
    )
  }

  const isOwner = club.role === 'owner'
  const year = new Date().getFullYear()
  const url = inviteUrl(club.inviteCode)
  const season = [...club.season].sort((a, b) => b.points - a.points)
  const otherMembers = club.members.filter((m) => m.userId !== currentUserId)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      showToast(texts.toasts.linkCopied)
    } catch {
      const el = linkRef.current
      if (el) {
        const range = document.createRange()
        range.selectNodeContents(el)
        const sel = window.getSelection()
        sel?.removeAllRanges()
        sel?.addRange(range)
        try {
          document.execCommand('copy')
          showToast(texts.toasts.linkCopied)
        } catch {
          // Kunne ikke kopiere - linket kan stadig vælges manuelt
        }
      }
    }
  }

  const handleRotate = async () => {
    setError('')
    try {
      const result = await rotateInvite(club.id)
      setClub({ ...club, inviteCode: result.inviteCode })
      setConfirmingRotate(false)
    } catch (err: any) {
      setError(err.response?.data?.message || texts.club.couldNotRotate)
    }
  }

  const handleInviteEmail = async (e: FormEvent) => {
    e.preventDefault()
    const trimmed = inviteEmail.trim()
    if (!trimmed || !club) return
    setInvitingEmail(true)
    try {
      const result = await inviteByEmail(club.id, trimmed)
      showToast(result.alreadyMember ? texts.club.inviteAlreadyMember : texts.club.inviteSent)
      setInviteEmail('')
    } catch (err: any) {
      showToast(err.response?.data?.message || texts.club.inviteError)
    } finally {
      setInvitingEmail(false)
    }
  }

  const handleAddCandidate = async (userId: number) => {
    if (!club) return
    setAddingUserId(userId)
    try {
      await addMember(club.id, userId)
      showToast(texts.club.memberAdded)
      load()
    } catch (err: any) {
      showToast(err.response?.data?.message || texts.club.couldNotAddMember)
    } finally {
      setAddingUserId(null)
    }
  }

  const startRename = () => {
    setRenameValue(club.name)
    setRenaming(true)
  }

  const handleRename = async () => {
    const trimmed = renameValue.trim()
    if (!trimmed || trimmed === club.name) {
      setRenaming(false)
      return
    }
    setError('')
    try {
      await renameClub(club.id, trimmed)
      setClub({ ...club, name: trimmed })
      setRenaming(false)
    } catch (err: any) {
      setError(err.response?.data?.message || texts.club.couldNotRename)
    }
  }

  const handleRemoveMember = async (userId: number) => {
    setError('')
    try {
      await removeMember(club.id, userId)
      setRemovingUserId(null)
      load()
    } catch (err: any) {
      setError(err.response?.data?.message || texts.club.couldNotRemove)
    }
  }

  const handleLeave = async () => {
    setError('')
    try {
      await leaveClub(club.id)
      window.location.href = '/klubber'
    } catch (err: any) {
      setError(err.response?.data?.message || texts.club.couldNotLeave)
      setConfirmingLeave(false)
    }
  }

  const handleTransfer = async () => {
    if (transferUserId == null) return
    setTransferring(true)
    try {
      await transferClub(club.id, transferUserId)
      showToast(texts.club.transferDone)
      setConfirmingTransfer(false)
      load()
    } catch (err: any) {
      showToast(err.response?.data?.message || texts.club.couldNotTransfer)
    } finally {
      setTransferring(false)
    }
  }

  const handleDeleteClub = async () => {
    setDeletingClub(true)
    try {
      await deleteClub(club.id)
      showToast(texts.club.deleteDone)
      navigate('/klubber')
    } catch (err: any) {
      showToast(err.response?.data?.message || texts.club.couldNotDeleteClub)
      setConfirmingDeleteClub(false)
      setDeletingClub(false)
    }
  }

  return (
    <Screen>
      {renaming ? (
        <div className="flex items-center gap-2">
          <Input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} autoFocus />
          <Button size="sm" onClick={handleRename}>
            {texts.club.renameSave}
          </Button>
        </div>
      ) : (
        <TopBar
          title={club.name}
          back="/klubber"
          right={
            isOwner && (
              <Button variant="ghost" size="sm" onClick={startRename}>
                {texts.club.rename}
              </Button>
            )
          }
        />
      )}

      <Panel>
        <b>{texts.club.inviteEmailTitle}</b>
        <form className="flex gap-2" onSubmit={handleInviteEmail}>
          <Input
            type="email"
            inputMode="email"
            placeholder={texts.club.inviteEmailPlaceholder}
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            required
          />
          <Button type="submit" disabled={invitingEmail}>
            {invitingEmail ? texts.club.inviteSending : texts.club.inviteEmailButton}
          </Button>
        </form>
      </Panel>

      {candidates.length > 0 && (
        <Panel>
          <b>{texts.club.candidatesTitle}</b>
          <div className="grid gap-1.5">
            {candidates.map((c, i) => (
              <div
                key={c.userId}
                className="flex items-center gap-2.5 rounded-xl border border-text-2/20 bg-text-2/10 px-3 py-2.5"
              >
                <Avatar name={c.name} index={i} />
                <div className="min-w-0 flex-1">
                  <b className="block">{c.name}</b>
                  {c.isGuest && <Chip variant="neutral">{texts.club.guestChip}</Chip>}
                </div>
                <button
                  type="button"
                  aria-label={texts.club.addMember}
                  onClick={() => handleAddCandidate(c.userId)}
                  disabled={addingUserId === c.userId}
                  className="grid h-11 w-11 flex-none place-items-center rounded-xl bg-gold text-xl font-bold text-ink disabled:opacity-45"
                >
                  +
                </button>
              </div>
            ))}
          </div>
        </Panel>
      )}

      <Panel>
        <div className="flex gap-3">
          <QrCode value={url} size={112} />
          <div className="grid gap-1">
            <b>{texts.club.inviteTitle}</b>
            <span className="text-[0.85em] text-text-2">{texts.club.inviteHint}</span>
            <span ref={linkRef} className="break-all font-mono text-[0.85em]">
              {url}
            </span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Button variant="ghost" size="sm" onClick={handleCopy}>
            {texts.newGame.copyLink}
          </Button>
          {isOwner &&
            (confirmingRotate ? (
              <div className="col-span-2 grid gap-2">
                <p className="text-[0.85em] text-red">{texts.club.newLinkConfirm}</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setConfirmingRotate(false)}>
                    {texts.club.cancel}
                  </Button>
                  <Button variant="danger" size="sm" onClick={handleRotate}>
                    {texts.club.newLinkConfirmButton}
                  </Button>
                </div>
              </div>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => setConfirmingRotate(true)}>
                {texts.club.newLink}
              </Button>
            ))}
        </div>
        {error && <p className="text-red">{error}</p>}
      </Panel>

      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[0.72rem] uppercase tracking-[0.08em] text-text-2">
          {texts.club.season(year)}
        </span>
        <CsvButton href={clubExportCsvUrl(clubId, year)} label={shareTexts.share.csvClubLabel} />
      </div>
      <div className="grid gap-2 rounded-2xl border border-text-2/20 bg-text-2/10 p-3.5">
        {season.map((entry, i) => (
          <div key={entry.userId} className="grid grid-cols-[22px_auto_1fr_auto] items-center gap-2.5">
            <span className="font-mono text-text-2">{i + 1}</span>
            <Avatar name={entry.name} index={i} />
            <span className="min-w-0 truncate">{entry.name}</span>
            <span className={`font-mono font-semibold ${entry.points > 0 ? 'text-mint' : entry.points < 0 ? 'text-red' : ''}`}>
              {fmt(entry.points)}
            </span>
          </div>
        ))}
      </div>

      <span className="font-mono text-[0.72rem] uppercase tracking-[0.08em] text-text-2">
        {texts.club.membersTitle}
      </span>
      <div className="grid gap-1.5">
        {club.members.map((member, i) => (
          <div
            key={member.userId}
            className="flex items-center gap-2.5 rounded-xl border border-text-2/20 bg-text-2/10 px-3 py-2.5"
          >
            <Avatar name={member.name} index={i} />
            <div className="min-w-0 flex-1">
              <b className="block">{member.name}</b>
              {member.isGuest && <Chip variant="neutral">{texts.club.guestChip}</Chip>}
            </div>
            {isOwner && member.userId !== currentUserId && (
              removingUserId === member.userId ? (
                <div className="grid gap-1.5">
                  <span className="text-[0.8em] text-red">{texts.club.removeConfirm(member.name)}</span>
                  <div className="flex gap-1.5">
                    <Button variant="ghost" size="sm" onClick={() => setRemovingUserId(null)}>
                      {texts.club.cancel}
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => handleRemoveMember(member.userId)}>
                      {texts.club.removeConfirmButton}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => setRemovingUserId(member.userId)}>
                  {texts.club.remove}
                </Button>
              )
            )}
          </div>
        ))}
      </div>

      {isOwner && (
        <Panel>
          <b>{texts.club.ownerTitle}</b>

          {otherMembers.length > 0 && (
            <div className="grid gap-1.5">
              <span className="text-[0.85em] text-text-2">{texts.club.transferTitle}</span>
              {confirmingTransfer ? (
                <div className="grid gap-2">
                  <p className="text-[0.85em] text-red">
                    {texts.club.transferConfirm(otherMembers.find((m) => m.userId === transferUserId)?.name ?? '')}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setConfirmingTransfer(false)}>
                      {texts.club.cancel}
                    </Button>
                    <Button variant="danger" size="sm" onClick={handleTransfer} disabled={transferring}>
                      {texts.club.transferConfirmButton}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <select
                    aria-label={texts.club.transferTitle}
                    value={transferUserId ?? otherMembers[0].userId}
                    onChange={(e) => setTransferUserId(Number(e.target.value))}
                    className="wb-input min-h-11 w-full rounded-xl border border-text-2/20 px-3 py-2.5 text-text"
                  >
                    {otherMembers.map((m) => (
                      <option key={m.userId} value={m.userId}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                  <Button
                    size="sm"
                    onClick={() => {
                      setTransferUserId(transferUserId ?? otherMembers[0].userId)
                      setConfirmingTransfer(true)
                    }}
                  >
                    {texts.club.transferButton}
                  </Button>
                </div>
              )}
            </div>
          )}

          <div className="grid gap-1.5">
            <span className="text-[0.85em] text-text-2">{texts.club.deleteTitle}</span>
            {confirmingDeleteClub ? (
              <div className="grid gap-2">
                <p className="text-[0.85em] text-red">{texts.club.deleteClubConfirm(club.name)}</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setConfirmingDeleteClub(false)}>
                    {texts.club.cancel}
                  </Button>
                  <Button variant="danger" size="sm" onClick={handleDeleteClub} disabled={deletingClub}>
                    {texts.club.deleteClubConfirmButton}
                  </Button>
                </div>
              </div>
            ) : (
              <Button variant="danger" size="sm" onClick={() => setConfirmingDeleteClub(true)}>
                {texts.club.deleteButton}
              </Button>
            )}
          </div>
        </Panel>
      )}

      {confirmingLeave ? (
        <Panel>
          <p className="text-[0.85em] text-red">{texts.club.leaveConfirm}</p>
          {error && <p className="text-red">{error}</p>}
          <div className="grid grid-cols-2 gap-2">
            <Button variant="ghost" onClick={() => setConfirmingLeave(false)}>
              {texts.club.cancel}
            </Button>
            <Button variant="danger" onClick={handleLeave}>
              {texts.club.leaveConfirmButton}
            </Button>
          </div>
        </Panel>
      ) : (
        <Button variant="ghost" full onClick={() => setConfirmingLeave(true)}>
          {texts.club.leave}
        </Button>
      )}

      <BottomNav current="clubs" />
    </Screen>
  )
}

export default Club
